import { Stack, Typography } from '@mui/material';
import {
  AssessmentEntryScore,
  AssessmentMethodScore,
  AssessmentMethodType,
  GET_ASSESSMENTS_ENTRIES_QUERY,
  useUpsertDeleteAssessmentEntryMutation,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  DEFAULT_NOTIFICATION_DURATION_MS,
  useNotifications,
} from '@schooly/components/notifications';
import { ColorCellWrapper } from '@schooly/style';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  CellColorProps,
  getCellTextColor,
} from '../../../../pages/Assessments/Markbook/common/colouredCells';
import { UpdateAssessmentEntryParams } from '../../../../pages/Assessments/Markbook/common/types';
import { FormTextFieldStyled } from '../../FormTextField/FormTextField.styled';
import { convertToAssessmentEntry, getScorePercentageValue } from '../utils';

const CELL_DEFAULT_WIDTH = 112;
const DEFAULT_SUBJECT_ID = '';
export interface ScoreControllerProps {
  groupId: string;
  relationId: string;
  entry?: AssessmentEntryScore;
  assessmentId: string;
  enterable: boolean;
  method: AssessmentMethodScore;
  onSuccess?: () => void;
  focused?: boolean;
  updateAssessmentEntry?: (p: UpdateAssessmentEntryParams) => void;
  width?: number;
  hover?: boolean;
  showPercentage?: boolean;
  getCellColor?: (cellProps: CellColorProps) => string | null;
}

/**
 * @deprecated Use ScoreCell component instead, example of usage see in markbook tables
 */
export const ScoreController: FC<ScoreControllerProps> = ({
  groupId,
  relationId,
  assessmentId,
  enterable,
  method,
  entry,
  onSuccess,
  focused,
  updateAssessmentEntry,
  width = CELL_DEFAULT_WIDTH,
  hover = true,
  showPercentage,
  getCellColor,
}) => {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const { showError } = useNotifications();
  const { permissions } = useAuth();
  const [isEdit, setIsEdit] = useState(false);
  const [value, setValue] = useState(entry?.score ?? '');
  const [lastValue, setLastValue] = useState(value);
  const [error, setError] = useState<any>();
  const escPressed = useRef(false);

  const upsertDeleteAssessmentEntry = useUpsertDeleteAssessmentEntryMutation();
  const canEdit = method.id && (permissions.includes('assessment_manager') || enterable);
  const scoreOutOf = method.score_out_of;
  const hasScoreOutOf = scoreOutOf != null;
  const hasValue = typeof value === 'number';
  const showPercentageValue = !!showPercentage && hasScoreOutOf && hasValue;
  const showScoreOutOf = hasScoreOutOf && !showPercentageValue;

  const handleFocus = useCallback(() => {
    if (focused === false) return;
    setIsEdit(true);
    const inputElement = inputRef.current?.querySelector('input');
    inputElement?.focus();
  }, [focused]);

  useEffect(() => {
    setValue(entry?.score ?? '');
  }, [entry?.score]);

  const methodId = method.id;

  const handleBlur = useCallback(async () => {
    setError(undefined);
    setIsEdit(false);

    if (!methodId) return;

    const currValue = value;
    if (lastValue !== value && !escPressed.current) {
      setError(undefined);
      const score = typeof currValue === 'number' ? currValue : null;

      upsertDeleteAssessmentEntry.mutate(
        {
          method_id: methodId,
          recipient_relation_id: relationId,
          data: {
            group_id: groupId,
            score,
          },
        },
        {
          onSuccess: (res) => {
            queryClient.invalidateQueries([GET_ASSESSMENTS_ENTRIES_QUERY]);

            updateAssessmentEntry?.({
              entry: res.assessment_entry ? convertToAssessmentEntry(res.assessment_entry) : null,
              assessmentId,
              methodId,
              studentId: relationId,
              groupId,
              methodType: AssessmentMethodType.Score,
            });

            setLastValue(currValue);
            onSuccess?.();
            escPressed.current = false;
          },

          onError: (err) => {
            console.error(err);
            setError(err);
            showError(err);

            setTimeout(() => {
              setError(undefined);
              setValue(lastValue);
            }, DEFAULT_NOTIFICATION_DURATION_MS);
          },
        },
      );
    }
  }, [
    methodId,
    value,
    lastValue,
    upsertDeleteAssessmentEntry,
    relationId,
    groupId,
    queryClient,
    updateAssessmentEntry,
    assessmentId,
    onSuccess,
    showError,
  ]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(undefined);

    const newValue = event.target.value;

    if (newValue === '' || newValue === '-' || /^\d*$/.test(newValue)) {
      setValue(newValue === '' || newValue === '-' ? '' : (Number.parseInt(newValue) ?? ''));
    }
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      let inputElement;

      switch (event.code) {
        case 'Enter':
          inputElement = inputRef.current?.querySelector('input');
          inputElement?.blur();
          break;
        case 'Escape':
          escPressed.current = true;
          setValue(lastValue);
          inputElement = inputRef.current?.querySelector('input');
          inputElement?.blur();
      }
    },
    [lastValue],
  );

  const color = entry
    ? getCellColor?.({
        assessmentId,
        groupId,
        entry,
        studentRelationId: relationId,
        subjectId: DEFAULT_SUBJECT_ID,
        method,
      })
    : null;
  const [textColor, scoreOutOfColor] = color ? getCellTextColor(color) : [];

  const isEmpty = value === '';

  const backgroundColor = useMemo(() => {
    if (!isEdit && error) return 'error.superLight';
    if (isEdit) return 'background.paper';
  }, [error, isEdit]);

  const clickHandler = useCallback(
    (e: React.MouseEvent) => {
      if (canEdit) {
        handleFocus();
      }
      if (focused) {
        e.stopPropagation();
      }
    },
    [canEdit, focused, handleFocus],
  );

  useEffect(() => {
    if (focused) {
      handleFocus();
    }
  }, [focused, handleFocus]);

  const scoreOutOfTextColor = scoreOutOfColor ?? 'text.secondary';
  const scoreTextColor = textColor ?? 'text.primary';

  return (
    <Stack onClick={clickHandler} sx={{ backgroundColor: backgroundColor }}>
      {method.id && (
        <Stack
          width="100%"
          height={!canEdit ? 44 : undefined}
          direction="row"
          alignItems="center"
          justifyContent={!isEmpty && showScoreOutOf ? 'flex-end' : 'center'}
          sx={(theme) => ({
            position: 'relative',
            paddingRight: showScoreOutOf && !isEdit && !isEmpty ? `${width / 2}px` : 0,
            //Active state of the cell
            border: isEdit ? theme.mixins.borderControlValue() : 'none',
            m: isEdit ? '-1px' : 0,
            ml: 0,
            zIndex: isEdit ? 2 : undefined,
          })}
        >
          {color && !error && <ColorCellWrapper color={color} />}

          {showScoreOutOf && (!isEmpty || isEdit) && (
            <Typography
              variant="caption"
              color={!isEdit && error ? 'error.main' : scoreOutOfTextColor}
              sx={{
                position: 'absolute',
                zIndex: 1,
                left: width / 2,
                top: 17,
                ...(hover && {
                  '.GridRow:hover &, .GridRowItem:hover &, .MuiDataGrid-row.Mui-hovered &': {
                    color: !error ? 'primary.main' : 'error.main',
                  },
                }),
              }}
            >
              /{method.score_out_of}
            </Typography>
          )}
          {canEdit && (
            <FormTextFieldStyled
              variant="standard"
              sx={(theme) => ({
                width: isEdit ? '100%' : 0,
                textAlign: hasScoreOutOf ? 'right' : 'center',
                paddingRight: hasScoreOutOf && isEdit ? `${width / 2}px` : 0,
                '& .MuiInputBase-root::before, & .MuiInputBase-root::after': {
                  border: 'none !important',
                },
                '& .MuiInput-input': {
                  textAlign: hasScoreOutOf ? 'right' : 'center',
                  ...theme.typography.h3,
                  height: '44px !important',
                  margin: '0 !important',
                },
              })}
              ref={inputRef}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              value={value}
            />
          )}
          {!isEdit && (
            <>
              {isEmpty ? (
                <Typography
                  variant="h3"
                  display="inline"
                  color={error ? 'error.main' : 'text.primary'}
                  sx={{
                    ...(hover && {
                      '.GridRow:hover &, .GridRowItem:hover &, .MuiDataGrid-row.Mui-hovered &': {
                        color: error ? 'error.main' : 'primary.main',
                      },
                    }),
                  }}
                >
                  -
                </Typography>
              ) : (
                <Typography
                  variant="h3"
                  display="inline-block"
                  color={error ? 'error.main' : scoreTextColor}
                  sx={{
                    textOverflow: 'ellipsis',
                    overflowX: 'hidden',
                    maxWidth: showScoreOutOf ? 45 : 90,
                  }}
                >
                  {showPercentageValue ? `${getScorePercentageValue(value, scoreOutOf)}%` : value}
                </Typography>
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};
